import type {
  InAppNotification,
  InAppNotificationsData,
  InAppNotificationsPageData,
} from '@/types/inAppNotification';

export const EMPTY_IN_APP_NOTIFICATIONS: InAppNotificationsData = {
  notifications: [],
  totalUnseen: 0,
};

type MergeResult = {
  data: InAppNotificationsData;
  /**
   * Notifications that were not already in the cache. Callers use this to avoid
   * re-announcing something the user has already been shown.
   */
  added: InAppNotification[];
};

/**
 * Newest first, matching the server's `order_by: [desc: :created_at, desc: :id]`.
 *
 * `createdAt` is nullable on the wire, so nulls are ordered last explicitly
 * rather than left to whatever a comparison against `null` happens to return.
 */
const byNewestFirst = (left: InAppNotification, right: InAppNotification) => {
  if (left.createdAt !== right.createdAt) {
    if (left.createdAt === null) {
      return 1;
    }

    if (right.createdAt === null) {
      return -1;
    }

    return left.createdAt < right.createdAt ? 1 : -1;
  }

  return right.id - left.id;
};

/**
 * Merges notifications into the cache entry, de-duplicating on `id`.
 *
 * De-duplication is load-bearing, not defensive: the backfill re-runs on every
 * socket rejoin and will legitimately return rows already merged from live
 * events, and an Oban job rescued by `Lifeline` can broadcast the same row
 * twice. Duplicate list items and an inflated badge are the visible symptoms.
 *
 * `totalUnseen` only ever moves by the number of genuinely new notifications,
 * because the server caps the list it returns but not the count it reports.
 */
export const mergeInAppNotifications = (
  current: InAppNotificationsData | undefined,
  incoming: InAppNotification[],
): MergeResult => {
  const existing = current ?? EMPTY_IN_APP_NOTIFICATIONS;
  const byId = new Map(existing.notifications.map((notification) => [notification.id, notification]));
  const added: InAppNotification[] = [];

  incoming.forEach((notification) => {
    // `added` drives both the badge increment and the toast, so only genuinely
    // unread arrivals belong in it. A seen notification still takes its place in
    // the list — the panel is a history — but it must not raise a toast for
    // something already read, nor count towards a badge that tracks unread only.
    if (!byId.has(notification.id) && notification.seenAt === null) {
      added.push(notification);
    }

    byId.set(notification.id, notification);
  });

  return {
    data: {
      notifications: [...byId.values()].sort(byNewestFirst),
      totalUnseen: existing.totalUnseen + added.length,
    },
    added,
  };
};

/**
 * Replaces the cache entry with a backfill response.
 *
 * Read and unread alike are kept: the response is the notification history the
 * panel renders, and `seenAt` on each row is what tells the two apart.
 *
 * The count comes from the server rather than being derived, because the list is
 * capped by `limit` while `meta.total_unseen` is not — and because the list now
 * holds read rows the count deliberately excludes.
 */
export const replaceInAppNotifications = (
  notifications: InAppNotification[],
  totalUnseen: number,
): InAppNotificationsData => ({
  notifications: [...notifications].sort(byNewestFirst),
  totalUnseen,
});

/**
 * Optimistically marks one notification read, in place.
 *
 * The row stays in the list — reading a notification is a state change, not a
 * dismissal, and the panel keeps showing it as history.
 *
 * The badge only moves if that row was actually unread. A notification can be
 * read twice over (the toast's View button, then the row itself, or a second tab),
 * and each of those paths calls this; without the guard the count would fall by
 * one per attempt and drift below the truth.
 */
export const markInAppNotificationSeenInCache = (
  current: InAppNotificationsData | undefined,
  id: number,
  seenAt: string = new Date().toISOString(),
): InAppNotificationsData => {
  const existing = current ?? EMPTY_IN_APP_NOTIFICATIONS;
  const target = existing.notifications.find((notification) => notification.id === id);

  if (!target || target.seenAt !== null) {
    return existing;
  }

  return {
    notifications: existing.notifications.map((notification) => (
      notification.id === id ? { ...notification, seenAt } : notification
    )),
    totalUnseen: Math.max(0, existing.totalUnseen - 1),
  };
};

/**
 * Optimistically marks every notification read, keeping them all listed.
 *
 * Already-read rows keep their original `seenAt`: the server records the
 * earliest sighting and ignores a later one, so overwriting here would make the
 * cache disagree with what the next backfill returns.
 */
export const markAllInAppNotificationsSeenInCache = (
  current: InAppNotificationsData | undefined,
  seenAt: string = new Date().toISOString(),
): InAppNotificationsData => {
  const existing = current ?? EMPTY_IN_APP_NOTIFICATIONS;

  return {
    notifications: existing.notifications.map((notification) => (
      notification.seenAt === null ? { ...notification, seenAt } : notification
    )),
    totalUnseen: 0,
  };
};

/**
 * Optimistically marks notifications read on a cached page of the
 * all-notifications view — one by `id`, or every unread row when `id` is omitted.
 *
 * The rows stay where they are, even under the unread filter, so nothing shifts
 * under the cursor; they drop out when that page is next fetched. As above, a row
 * already read keeps its original `seenAt`.
 */
export const markInAppNotificationsSeenInPage = (
  current: InAppNotificationsPageData | undefined,
  id?: number,
  seenAt: string = new Date().toISOString(),
): InAppNotificationsPageData | undefined => {
  if (!current) {
    return current;
  }

  return {
    ...current,
    notifications: current.notifications.map((notification) => (
      notification.seenAt === null && (id === undefined || notification.id === id)
        ? { ...notification, seenAt }
        : notification
    )),
  };
};
